import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import {
  formatMicrophoneSignalPower,
  getMicrophoneFrequencyCalibration,
  resolveMicrophoneCalibration,
} from "./microphoneCalibrationUtils";

function sortCalibrationsByFrequency(calibrations) {
  if (!Array.isArray(calibrations)) {
    return [];
  }

  return calibrations.slice().sort((a, b) => a.freq - b.freq);
}

function formatMaxIntensity(value) {
  if (value == null || Number.isNaN(value)) {
    return "";
  }

  return Math.round(value * 10) / 10;
}

export default function RawCalibrationDetailsTable({
  calibrations,
  microphoneCalibration,
  isDetailedData,
  showMicrophoneSignal = true,
}) {
  const sortedCalibrations = useMemo(
    () => sortCalibrationsByFrequency(calibrations),
    [calibrations],
  );
  const calibrationsByFrequency = useMemo(
    () =>
      new Map(
        sortedCalibrations.map((calibration) => [
          Number(calibration.freq),
          calibration,
        ]),
      ),
    [sortedCalibrations],
  );
  const displayedFrequencies = useMemo(() => {
    const frequencies = sortedCalibrations.map(({ freq }) => Number(freq));

    if (isDetailedData && showMicrophoneSignal) {
      const resolvedMicrophoneCalibration = resolveMicrophoneCalibration(
        microphoneCalibration,
      );
      const microphoneFrequencies =
        resolvedMicrophoneCalibration?.freqs ||
        resolvedMicrophoneCalibration?.frequencies ||
        [];

      microphoneFrequencies.forEach((entry) => {
        frequencies.push(Number(entry?.freq ?? entry?.frequency));
      });
    }

    return [...new Set(frequencies.filter(Number.isFinite))].sort(
      (left, right) => left - right,
    );
  }, [
    isDetailedData,
    microphoneCalibration,
    showMicrophoneSignal,
    sortedCalibrations,
  ]);

  if (sortedCalibrations.length === 0) {
    return null;
  }

  return (
    <Table size="small" aria-label="calibration table">
      <TableHead>
        <TableRow>
          <TableCell>Parametr</TableCell>
          {displayedFrequencies.map((frequency) => (
            <TableCell key={frequency} align="center">
              {frequency} Hz
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {isDetailedData && (
          <>
            <TableRow>
              <TableCell component="th" scope="row">
                Głośność aplikacji:
              </TableCell>
              {displayedFrequencies.map((frequency) => (
                <TableCell key={frequency} align="center">
                  {calibrationsByFrequency.get(frequency)?.appVolume ?? ""}
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell component="th" scope="row">
                Testowane względem [dB SPL]:
              </TableCell>
              {displayedFrequencies.map((frequency) => (
                <TableCell key={frequency} align="center">
                  {calibrationsByFrequency.get(frequency)
                    ?.intensityTestedAgainst ?? ""}
                </TableCell>
              ))}
            </TableRow>

            <TableRow>
              <TableCell component="th" scope="row">
                Głośność główna:
              </TableCell>
              {displayedFrequencies.map((frequency) => (
                <TableCell key={frequency} align="center">
                  {calibrationsByFrequency.get(frequency)?.masterVolume ?? ""}
                </TableCell>
              ))}
            </TableRow>
          </>
        )}

        <TableRow>
          <TableCell component="th" scope="row">
            Maksymalna głośność aplikacji [dB SPL]:
          </TableCell>
          {displayedFrequencies.map((frequency) => (
            <TableCell key={frequency} align="center">
              {formatMaxIntensity(
                calibrationsByFrequency.get(frequency)?.maxIntensity,
              )}
            </TableCell>
          ))}
        </TableRow>

        {isDetailedData && showMicrophoneSignal && (
          <TableRow>
            <TableCell component="th" scope="row">
              Siła sygnału mikrofonu:
            </TableCell>
            {displayedFrequencies.map((frequency) => {
              const microphoneFrequencyCalibration =
                getMicrophoneFrequencyCalibration(
                  microphoneCalibration,
                  frequency,
                );

              return (
                <TableCell key={frequency} align="center">
                  {formatMicrophoneSignalPower(
                    microphoneFrequencyCalibration?.measuredSignalPower,
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
